import React, { useState, useEffect, useMemo } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import MDTypography from "components/MDTypography";
import { Select, MenuItem, FormControl, InputLabel, TextField, Grid } from "@mui/material";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import DataTable from "examples/Tables/DataTable";
import jsPDF from "jspdf";
import "jspdf-autotable";
import MDButton from "components/MDButton";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import { disableBackNavigation } from "core/utils/storage";
import api from "core/interceptors/api";
import moment from "moment"; // Ensure moment is imported

function SalesReport() {
    const [selectedOption, setSelectedOption] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [tableData, setTableData] = useState({ columns: [], rows: [] });
    const [userEmail, setUserEmail] = useState('');
    const [orderStatus, setOrderStatus] = useState('');

    const navigate = useNavigate();

    const orderStatuses = useMemo(() => ([
        { label: 'Pending', value: 'pending' },
        { label: 'Processing', value: 'processing' },
        { label: 'Shipped', value: 'shipped' },
        { label: 'Delivered', value: 'delivered' },
        { label: 'Cancelled', value: 'cancelled' },
    ]), []); // Memoize to prevent unnecessary re-renders

    // Define columns dynamically based on selected report type
    const columns = useMemo(() => {
        switch (selectedOption) {
            case 'userOrdersByDate':
                return [
                    { Header: "S.No", accessor: "serial_no" },
                    { Header: "Order ID", accessor: "id" },
                    { Header: "User ID", accessor: "user_id" },
                    { Header: "Order Date", accessor: "order_date" },
                    { Header: "Total Amount", accessor: "total_amount" },
                    { Header: "Order Status", accessor: "order_status" },
                    { Header: "Payment Status", accessor: "payment_status" },
                    { Header: "Delivery Address", accessor: "delivery_address" },
                ];
            case 'highestSoldProductAPI':
                return [
                    { Header: "S.No", accessor: "serial_no" },
                    { Header: "Product Name", accessor: "product_name" },
                    { Header: "Total Quantity Sold", accessor: "total_quantity_sold_across_all_orders" },
                    { Header: "Customer ID", accessor: "customer_id" },
                    { Header: "Customer Name", accessor: "customer_name" },
                    { Header: "Orders Count", accessor: "order_count" },
                    { Header: "Total Quantity Bought by Customer", accessor: "total_quantity_bought" },
                    { Header: "Order IDs", accessor: "order_ids" },
                ];
            case 'deliveredOrdersByDate':
                return [
                    { Header: "S.No", accessor: "serial_no" },
                    { Header: "Order ID", accessor: "id" },
                    { Header: "User ID", accessor: "user_id" },
                    { Header: "Order Date", accessor: "order_date" },
                    { Header: "Delivery Date", accessor: "delivery_date" },
                    { Header: "Total Amount", accessor: "total_amount" },
                    { Header: "Payment Status", accessor: "payment_status" },
                    { Header: "Delivery Address", accessor: "delivery_address" },
                ];
            default:
                return [];
        }
    }, [selectedOption]);

    useEffect(() => {
        disableBackNavigation();
        // **IMPORTANT**: Ensure this key ('userEmail') matches how you store the email during login.
        // If your login stores it as 'email', change 'userEmail' to 'email' here.
        const storedEmail = localStorage.getItem('userEmail');
        if (storedEmail) {
            setUserEmail(storedEmail);
        } else {
            console.warn("User email not found in localStorage. 'Orders by Specific User' report might not function.");
        }
    }, []);

    const handleChange = (event) => {
        setSelectedOption(event.target.value);
        setTableData({ columns: [], rows: [] });
        setStartDate(null);
        setEndDate(null);
        setOrderStatus('');
    };

    // --- API Data Fetching Functions ---

    const fetchUserOrdersReportData = async (userId, start, end, status) => {
        setTableData({ columns: columns, rows: [] }); // Set columns immediately for display
        try {
            const params = {
                start_date: moment(start).format('YYYY-MM-DD'),
                end_date: moment(end).format('YYYY-MM-DD')
            };
            if (status) {
                params.order_status = status;
            }
            const response = await api.get(`/reports/orders/user/${userId}`, { params });

            if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                const mappedRows = response.data.map((order, index) => ({
                    serial_no: index + 1,
                    id: order.id || 'N/A',
                    user_id: order.user_id || 'N/A',
                    order_date: moment(order.order_date).isValid() ? moment(order.order_date).format('YYYY-MM-DD HH:mm:ss') : 'N/A',
                    total_amount: order.total_amount !== undefined && order.total_amount !== null ? `$${parseFloat(order.total_amount).toFixed(2)}` : 'N/A',
                    order_status: order.order_status || 'N/A',
                    payment_status: order.payment_status || 'N/A',
                    delivery_address: order.delivery_address || 'N/A',
                }));
                setTableData({ columns: columns, rows: mappedRows });
            } else {
                setTableData({ columns: columns, rows: [] });
            }
        } catch (error) {
            console.error("Error fetching orders by user report:", error);
            setTableData({ columns: columns, rows: [] });
            alert("Failed to fetch user order data. Please check user ID and try again.");
        }
    };

    const fetchHighestSoldProductReportData = async () => {
        setTableData({ columns: columns, rows: [] }); // Set columns immediately for display
        try {
            const response = await api.get('/reports/products/highest_sold');

            if (response.data && !response.data.message) {
                const { highest_sold_product, customers_who_bought_this_product } = response.data;

                if (highest_sold_product && customers_who_bought_this_product && customers_who_bought_this_product.length > 0) {
                    const mappedRows = customers_who_bought_this_product.map((customer, index) => ({
                        serial_no: index + 1,
                        product_name: highest_sold_product.product_name || 'N/A',
                        total_quantity_sold_across_all_orders: highest_sold_product.total_quantity_sold_across_all_orders !== undefined ? highest_sold_product.total_quantity_sold_across_all_orders : 'N/A',
                        customer_id: customer.customer_id || 'N/A',
                        customer_name: customer.customer_name || 'N/A',
                        order_count: customer.order_count !== undefined ? customer.order_count : 'N/A',
                        total_quantity_bought: customer.total_quantity_bought !== undefined ? customer.total_quantity_bought : 'N/A',
                        order_ids: customer.order_ids ? customer.order_ids.join(', ') : 'N/A',
                    }));
                    setTableData({ columns: columns, rows: mappedRows });
                } else if (highest_sold_product) {
                    setTableData({
                        columns: columns,
                        rows: [{
                            serial_no: 1,
                            product_name: highest_sold_product.product_name || 'N/A',
                            total_quantity_sold_across_all_orders: highest_sold_product.total_quantity_sold_across_all_orders !== undefined ? highest_sold_product.total_quantity_sold_across_all_orders : 'N/A',
                            customer_id: 'N/A',
                            customer_name: 'No customers found for this product',
                            order_count: 'N/A',
                            total_quantity_bought: 'N/A',
                            order_ids: 'N/A',
                        }]
                    });
                } else {
                    setTableData({ columns: columns, rows: [] });
                }
            } else {
                setTableData({ columns: columns, rows: [] });
            }
        } catch (error) {
            console.error("Error fetching highest sold product report:", error);
            setTableData({ columns: columns, rows: [] });
            alert("Failed to fetch highest sold product data. Please try again.");
        }
    };

    const fetchDeliveredOrdersReportData = async (start, end) => {
        setTableData({ columns: columns, rows: [] }); // Set columns immediately for display
        try {
            const params = {
                start_date: moment(start).format('YYYY-MM-DD'),
                end_date: moment(end).format('YYYY-MM-DD')
            };
            const response = await api.get('/reports/orders/delivered_between_dates', { params });

            if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                const mappedRows = response.data.map((order, index) => ({
                    serial_no: index + 1,
                    id: order.id || 'N/A',
                    user_id: order.user_id || 'N/A',
                    order_date: moment(order.order_date).isValid() ? moment(order.order_date).format('YYYY-MM-DD HH:mm:ss') : 'N/A',
                    delivery_date: moment(order.delivery_date).isValid() ? moment(order.delivery_date).format('YYYY-MM-DD HH:mm:ss') : 'N/A',
                    total_amount: order.total_amount !== undefined && order.total_amount !== null ? `$${parseFloat(order.total_amount).toFixed(2)}` : 'N/A',
                    payment_status: order.payment_status || 'N/A',
                    delivery_address: order.delivery_address || 'N/A',
                }));
                setTableData({ columns: columns, rows: mappedRows });
            } else {
                setTableData({ columns: columns, rows: [] });
            }
        } catch (error) {
            console.error("Error fetching delivered orders report:", error);
            setTableData({ columns: columns, rows: [] });
            alert("Failed to fetch delivered orders data. Please try again.");
        }
    };

    const handleFetchData = () => {
        // Validation for temporary disablement is handled by isGenerateButtonDisabled
        if (!selectedOption) {
            alert("Please select a report type.");
            return;
        }

        const requiresDates = ['userOrdersByDate', 'deliveredOrdersByDate'];
        if (requiresDates.includes(selectedOption) && (!startDate || !endDate)) {
            alert("Please select both a start date and an end date.");
            return;
        }

        switch (selectedOption) {
            case 'userOrdersByDate':
                if (!userEmail) {
                    alert("User email not found. Please ensure you are logged in or user email is in localStorage.");
                    return;
                }
                fetchUserOrdersReportData(userEmail, startDate, endDate, orderStatus);
                break;
            case 'highestSoldProductAPI':
                fetchHighestSoldProductReportData();
                break;
            case 'deliveredOrdersByDate':
                fetchDeliveredOrdersReportData(startDate, endDate);
                break;
            default:
                setTableData({ columns: [], rows: [] });
                break;
        }
    };

    const handleExportPDF = () => {
        if (tableData.rows.length === 0) {
            alert("No data to export. Please generate a report first.");
            return;
        }

        const doc = new jsPDF('l', 'mm', 'a4');
        let reportTitle = "";
        let fileName = "";

        switch (selectedOption) {
            case 'userOrdersByDate':
                reportTitle = `Orders for User: ${userEmail} (${orderStatus ? orderStatus : 'All'} Statuses)`;
                fileName = `user-orders-${userEmail}-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}.pdf`;
                break;
            case 'highestSoldProductAPI':
                reportTitle = "Highest Sold Product and Customers Report";
                fileName = `highest-sold-product-report.pdf`;
                break;
            case 'deliveredOrdersByDate':
                reportTitle = "Delivered Orders Report";
                fileName = `delivered-orders-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}.pdf`;
                break;
            default:
                reportTitle = "Generated Report";
                fileName = "report.pdf";
                break;
        }

        doc.text(reportTitle, 14, 15);
        doc.autoTable({
            theme: "striped",
            head: [columns.map(col => col.Header)],
            body: tableData.rows.map(row => columns.map(col => {
                if (col.accessor === 'order_ids' && Array.isArray(row[col.accessor])) {
                    return row[col.accessor].join(', ');
                }
                return row[col.accessor];
            })),
            startY: 20,
        });
        doc.save(fileName);
    };

    // Conditional rendering logic for date pickers and order status filters
    const shouldShowDatePickers = useMemo(() => {
        return ['userOrdersByDate', 'deliveredOrdersByDate'].includes(selectedOption);
    }, [selectedOption]);

    const shouldShowOrderStatusSelector = useMemo(() => {
        return selectedOption === 'userOrdersByDate';
    }, [selectedOption]);

    // New logic for temporary disablement
    // **Adjust this date** to control how long the button is disabled.
    // Current time is Thursday, July 17, 2025 at 8:22:11 PM IST.
    // Setting it to July 20, 2025, 8:00 AM IST (adjust as needed for your "few days").
    const disableUntilDate = useMemo(() => moment("2025-07-20T08:00:00+05:30").toDate(), []);

    const isTemporarilyDisabled = useMemo(() => {
        return new Date() < disableUntilDate;
    }, [disableUntilDate]);

    // Determine if the "Generate Report" button should be disabled
    const isGenerateButtonDisabled = useMemo(() => {
        if (isTemporarilyDisabled) {
            return true; // Disabled due to temporary flag
        }
        if (!selectedOption) {
            return true; // Disabled if no report type is selected
        }
        // Disabled if date-dependent report is selected but dates are missing
        if (shouldShowDatePickers && (!startDate || !endDate)) {
            return true;
        }
        // Disabled for 'userOrdersByDate' if userEmail is missing
        if (selectedOption === 'userOrdersByDate' && !userEmail) {
            return true;
        }
        return false; // Enabled otherwise
    }, [selectedOption, startDate, endDate, shouldShowDatePickers, userEmail, isTemporarilyDisabled]);


    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton
                    onLogout={() => {
                        navigate("/login");
                    }}
                />
            </MDBox>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                                <MDTypography variant="h6" color="white">Sales Reports</MDTypography>
                            </MDBox>
                            <MDBox pt={3} pl={3} pb={1}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <InputLabel>Select Report Type</InputLabel>
                                                <Select
                                                    label="Select Report Type"
                                                    name="report-type-select"
                                                    value={selectedOption}
                                                    onChange={handleChange}
                                                    sx={{
                                                        height: '45px',
                                                        width: '250px',
                                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'black' }
                                                    }}
                                                >
                                                    <MenuItem value="">-- Select Report Type --</MenuItem>
                                                    <MenuItem value="userOrdersByDate">Orders by a Specific User</MenuItem>
                                                    <MenuItem value="highestSoldProductAPI">Highest Sold Product</MenuItem>
                                                    <MenuItem value="deliveredOrdersByDate">Delivered Orders by Date</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {shouldShowDatePickers && (
                                            <>
                                                <Grid item xs={12} sm={6}>
                                                    <DatePicker
                                                        label="Start Date"
                                                        value={startDate}
                                                        onChange={(date) => setStartDate(date)}
                                                        renderInput={(params) => <TextField {...params} fullWidth />}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <DatePicker
                                                        label="End Date"
                                                        value={endDate}
                                                        onChange={(date) => setEndDate(date)}
                                                        renderInput={(params) => <TextField {...params} fullWidth />}
                                                    />
                                                </Grid>
                                            </>
                                        )}

                                        {shouldShowOrderStatusSelector && (
                                            <Grid item xs={12}>
                                                <FormControl fullWidth margin="normal">
                                                    <InputLabel>Select Order Status</InputLabel>
                                                    <Select
                                                        label="Select Order Status"
                                                        name="order-status-select"
                                                        value={orderStatus}
                                                        onChange={(e) => setOrderStatus(e.target.value)}
                                                        sx={{ height: '45px', width: '250px', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'black' } }}
                                                    >
                                                        <MenuItem value="">All Statuses</MenuItem>
                                                        {orderStatuses.map((status) => (
                                                            <MenuItem key={status.value} value={status.value}>
                                                                {status.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        )}

                                        <Grid item xs={12}>
                                            <MDButton
                                                variant="contained"
                                                color="success"
                                                onClick={handleFetchData}
                                                sx={{ mr: 1 }}
                                                disabled={isGenerateButtonDisabled} // Apply the combined disable logic
                                            >
                                                Generate Report
                                            </MDButton>
                                            {tableData.columns.length > 0 && tableData.rows.length > 0 && (
                                                <MDButton onClick={handleExportPDF} variant="contained" color="error">
                                                    Export as PDF
                                                </MDButton>
                                            )}
                                        </Grid>
                                        {isTemporarilyDisabled && (
                                            <Grid item xs={12}>
                                                <MDTypography variant="body2" color="warning" align="center" mt={2}>
                                                    Report generation is temporarily disabled until Due Less Days on Sales.
                                                </MDTypography>
                                            </Grid>
                                        )}
                                        {tableData.columns.length > 0 && tableData.rows.length > 0 ? (
                                            <Grid item xs={12}>
                                                <DataTable
                                                    table={tableData}
                                                    isSorted={true}
                                                    entriesPerPage={true}
                                                    showTotalEntries={true}
                                                    canSearch={true}
                                                    noEndBorder
                                                />
                                            </Grid>
                                        ) : (
                                            selectedOption && !isTemporarilyDisabled && (
                                                <Grid item xs={12}>
                                                    <MDTypography variant="body2" color="textSecondary" align="center" mt={2}>
                                                        No data found for the selected criteria.
                                                    </MDTypography>
                                                </Grid>
                                            )
                                        )}
                                    </Grid>
                                </LocalizationProvider>
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    );
}

export default SalesReport;